import {
  Dialog,
  DialogTrigger,
  Modal,
  Button,
} from 'react-aria-components'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Button as CalabrioButton, Checkbox } from '@calabriocloud/beacon-ui'
import { UserPrefsColumn } from '../constants'
import { useTranslation } from 'react-i18next'
import { useUpdateContactQueuePreference } from '../api'
import { kebabCase } from 'lodash'
import { Settings2 } from 'lucide-react'
import { convertToColumnDataJson } from '../utils'

interface ColumnSettingsProps {
  columns: UserPrefsColumn[],
}

export const ColumnSettings = React.memo(({ columns }: ColumnSettingsProps): ReactElement => {
  const { t } = useTranslation()
  const formRef = useRef<HTMLFormElement>(null)
  const [updateContactQueuePreference] = useUpdateContactQueuePreference()

  const [columnState, setColumnState] = useState<UserPrefsColumn[]>([...columns])
  useEffect(() => {
    if (columns?.length) {
      setColumnState([...columns])
    }
  }, [columns])

  return (
    <DialogTrigger>
      <Button
        aria-label={t('settings')}
        className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
        data-testid="column-settings-trigger"
      >
        <Settings2 className="w-5 h-5 text-gray-700" data-testid="column-settings-icon" />
      </Button>
      <Modal
        className="custom-modal"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '1.5rem',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          borderRadius: 8,
          zIndex: 1000,
        }}
        data-testid="column-settings-modal"
      >
        <Dialog>
          {({ close }) => {
            const handleSaveAndClose = async () => {
              if (!formRef.current) return
              try {
                await updateContactQueuePreference(convertToColumnDataJson(columnState))
                close()
              } catch (error) {
                throw new Error('Failed to save:' + error)
              }
            }

            // Check if localColumns is empty and set it to columns if it is
            return (
              <form ref={formRef} data-testid="column-settings-form">
                <h2 className="text-lg font-semibold mb-4" data-testid="column-settings-title">
                  {t('showHideColumns')}
                </h2>
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4"
                  data-testid="column-settings-checkbox-grid"
                >
                  {columnState?.map((column, index) => (
                    <Checkbox
                      key={index}
                      name="selectedColumns"
                      value={JSON.stringify(column)}
                      isSelected={column.isSelected}
                      className="flex items-center gap-2"
                      data-testid={`column-settings-checkbox-${kebabCase(column.label) || index}`}
                      onChange={isSelected => {
                        setColumnState(prevState => {
                          const updated = [...prevState]
                          updated[index] = { ...column, isSelected }
                          return updated
                        })
                      }}
                    >
                      <span data-testid={`column-settings-checkbox-label-${column.label || index}`}>
                        {t(column.label)}
                      </span>
                    </Checkbox>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-4" data-testid="column-settings-actions">
                  <CalabrioButton
                    variant='secondary'
                    onPress={close}
                    data-testid="column-settings-cancel"
                  >
                    {t('cancel')}
                  </CalabrioButton>
                  <CalabrioButton
                    onPress={handleSaveAndClose}
                    data-testid="column-settings-save"
                  >
                    {t('save')}
                  </CalabrioButton>
                </div>
              </form>
            )
          }}
        </Dialog>
      </Modal>
    </DialogTrigger>
  )
})

ColumnSettings.displayName = 'ColumnSettings'
